import Vue, { PropType } from 'vue';
import { Model } from '@viddsee/server-api-client';
import { VideoPlayerError } from '@/scripts/store/modules/video/state';
import { VideoOverlay } from '@/scripts/store/modules/video/state/VideoOverlay';
import { AnalyticUiEvent } from '@/scripts/libs/analytic/types';

export interface EmitPayload {
  /**
   * Emit receiver should operate on this specific overlay when called.
   */
  overlay: VideoOverlay;

  tracker?: Omit<AnalyticUiEvent, 'label' | 'action'> & {
    /**
     * Override the default tracker label
     * @default "action" // if emitted from action event
     * @default "dismiss" // if emitted from dismiss event
     * @default overlay.name // if emitted from shown event
     */
    label?: string;

    /**
     * Override the default tracker action
     * @default "click-button" // if emitted from action / dismiss event
     * @default "shown" // if emitted from shown event
     */
    action?: string;
  };
}

export type VideoPlayerOverlayShownPayload = EmitPayload

export interface VideoPlayerOverlayPerformActionPayload extends EmitPayload {
  fn?: Function;
}

export interface VideoPlayerOverlayDismissPayload extends EmitPayload {
  /**
   * Boolean - Should the video automatically start playing after the overlay is dismissed?
   * @default false
   */
  autoplay?: boolean;
}

const withOverlay = <T>(thisOverlay: VideoOverlay, payload?: T): T & EmitPayload =>
  Object.assign(
    {
      overlay: { ...thisOverlay },
    },
    payload,
  )
;

/**
 * The base component that provides
 * - props
 *   - video
 *   - error
 * - methods
 *   - dismiss
 *   - requestReverification
 */
export const VideoPlayerOverlayBase = Vue.extend({
  props: {
    video: {
      type: Object as PropType<Model.Video.Info>,
      required: true,
    },
    error: {
      type: Object as PropType<VideoPlayerError | null>,
      default: null,
    },
    overlay: {
      type: Object as PropType<VideoOverlay>,
      required: true,
    },
  },

  mounted() {
    this.$emit('shown', withOverlay(this.overlay));
  },

  methods: {
    dismiss(payload: Omit<VideoPlayerOverlayDismissPayload, 'overlay'>): void {
      this.$emit('dismiss', withOverlay(this.overlay, payload));
    },

    performAction(payload?: Omit<VideoPlayerOverlayPerformActionPayload, 'overlay'>): void {
      this.$emit('action', withOverlay(this.overlay, payload));
      return payload?.fn?.();
    },
  },
});
